var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { msg, str, localized } from '@lit/localize';
import '../form/ColorPicker.js';
import '../icon/Icon.js';
const DEFAULT_PIN_COLOR = '#1b96ff';
const PIN_FILL_ALPHA = 0.7; // 70% fill; stroke stays at 100%
/** Parse `#rrggbb` or `#rgb` into an rgba() string with the given alpha. */
function hexToRgba(hex, alpha) {
    const clean = hex.replace('#', '').trim();
    const full = clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean.padEnd(6, '0').slice(0, 6);
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) {
        return `rgba(27, 150, 255, ${alpha})`;
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
let ZoneCard = class ZoneCard extends LitElement {
    constructor() {
        super(...arguments);
        this.selected = false;
        this.hidden = false;
        this.pickerOpen = false;
        // Close picker on outside clicks (installed only while open).
        this.outsideClickListener = (e) => {
            if (!this.pickerOpen)
                return;
            // e.composedPath() crosses shadow boundaries; if the click is outside
            // this card's subtree, close.
            if (!e.composedPath().includes(this)) {
                this.pickerOpen = false;
                document.removeEventListener('mousedown', this.outsideClickListener);
            }
        };
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this.outsideClickListener);
    }
    getZoneColor() {
        const props = this.zone?.properties;
        const c = props?.color;
        return typeof c === 'string' && c.trim() ? c.trim() : DEFAULT_PIN_COLOR;
    }
    handleCardClick(e) {
        // Don't bubble card-click when the interaction was on the pin/picker or
        // the visibility button.
        const target = e.composedPath()[0];
        if (target?.closest?.('.pin, color-picker, .visibility-btn'))
            return;
        this.dispatchEvent(new CustomEvent('zone-click', {
            detail: this.zone,
            bubbles: true,
            composed: true,
        }));
    }
    handleVisibilityClick(e) {
        e.stopPropagation();
        const nextHidden = !this.hidden;
        this.dispatchEvent(new CustomEvent('zone-visibility-toggle', {
            detail: { zone: this.zone, hidden: nextHidden },
            bubbles: true,
            composed: true,
        }));
    }
    renderEyeIcon() {
        return html `<basic-icon
      .name=${this.hidden ? 'eye-off' : 'eye'}
      size="16"
    ></basic-icon>`;
    }
    handlePinClick(e) {
        e.stopPropagation();
        this.pickerOpen = !this.pickerOpen;
        if (this.pickerOpen) {
            document.addEventListener('mousedown', this.outsideClickListener);
        }
        else {
            document.removeEventListener('mousedown', this.outsideClickListener);
        }
    }
    handleColorChange(e) {
        const color = e.detail.color;
        // Mutate the local zone object so the pin updates immediately without
        // waiting for parent round-trip. Parent still receives the event and
        // can persist when the user saves.
        const nextProps = { ...(this.zone.properties ?? {}), color };
        this.zone = { ...this.zone, properties: nextProps };
        this.pickerOpen = false;
        document.removeEventListener('mousedown', this.outsideClickListener);
        this.dispatchEvent(new CustomEvent('zone-color-change', {
            detail: { zone: this.zone, color },
            bubbles: true,
            composed: true,
        }));
    }
    render() {
        const { zone_id, zone_type_id, parent_zone_id } = this.zone;
        const extras = this.zone;
        const displayName = extras.name?.trim() ||
            msg(str `Zone #${zone_id}`, {
                id: 'zoneCard.fallbackName',
                desc: 'Fallback display name for a zone with no name, using its id',
            });
        const color = this.getZoneColor();
        const pinStyle = `--zone-pin-stroke: ${color}; --zone-pin-fill: ${hexToRgba(color, PIN_FILL_ALPHA)};`;
        return html `
      <div class="layout" @click=${this.handleCardClick}>
        <div class="pin-wrap">
          <div
            class="pin"
            style=${pinStyle}
            role="button"
            tabindex="0"
            title=${msg('Change color', { id: 'zoneCard.action.changeColor', desc: 'Tooltip for the color swatch that opens the zone color picker' })}
            @click=${this.handlePinClick}
          ></div>
          <color-picker
            .color=${color}
            ?open=${this.pickerOpen}
            @color-change=${this.handleColorChange}
          ></color-picker>
        </div>

        <div class="body">
          <div class="header">
            <div class="title-col">
              <div class="name" title=${displayName}>${displayName}</div>
            </div>
            <div class="type-col">
              <span class="type-badge" title=${this.zoneTypeName ?? ''}>
                ${this.zoneTypeName ??
            msg(str `type ${zone_type_id}`, {
                id: 'zoneCard.typeFallback',
                desc: 'Fallback zone-type badge using the raw type id when no resolved name is available',
            })}
              </span>
              ${this.geolevelName
            ? html `<span class="geolevel" title=${this.geolevelName}>
                    ${this.geolevelName}
                  </span>`
            : null}
            </div>
            <button
              class="visibility-btn"
              type="button"
              title=${this.hidden
            ? msg('Show on map', { id: 'card.visibility.show', desc: 'Tooltip for the button that makes a zone visible on the map' })
            : msg('Hide from map', { id: 'card.visibility.hide', desc: 'Tooltip for the button that hides a zone from the map' })}
              aria-pressed=${this.hidden ? 'true' : 'false'}
              @click=${this.handleVisibilityClick}
            >
              ${this.renderEyeIcon()}
            </button>
          </div>

          <div class="meta">
            <span>#<strong>${zone_id}</strong></span>
            ${parent_zone_id != null
            ? html `<span class="sep">·</span>
                  <span
                    >${msg('parent', { id: 'zoneCard.meta.parent', desc: 'Label preceding the parent zone id in the zone metadata line' })}
                    <strong>${parent_zone_id}</strong></span
                  >`
            : null}
          </div>

          ${extras.description?.trim()
            ? html `<div class="description">${extras.description}</div>`
            : null}
        </div>
      </div>
    `;
    }
};
ZoneCard.styles = css `
    :host {
      display: block;
      padding: 12px 16px;
      cursor: pointer;
      border-left: 3px solid transparent;
      transition:
        background 0.15s,
        border-color 0.15s;
      font-family: inherit;
      color: #333;
      box-sizing: border-box;
      user-select: none;
      position: relative;
    }

    :host(:hover) {
      background: #f8fbff;
    }

    :host([selected]) {
      background: #e6f2ff;
      border-left-color: #0078d4;
    }

    :host([dragging]) {
      opacity: 0.4;
    }

    :host([drop-before]) {
      box-shadow: inset 0 2px 0 0 #0078d4;
    }

    :host([drop-after]) {
      box-shadow: inset 0 -2px 0 0 #0078d4;
    }

    .layout {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .pin-wrap {
      position: relative;
      flex-shrink: 0;
      margin-top: 5px;
    }

    .pin {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid var(--zone-pin-stroke, #1b96ff);
      background: var(--zone-pin-fill, rgba(27, 150, 255, 0.7));
      cursor: pointer;
      transition: transform 0.1s;
    }

    .pin:hover {
      transform: scale(1.15);
    }

    color-picker {
      top: 20px;
      left: 0;
    }

    .body {
      flex: 1;
      min-width: 0;
    }

    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }

    .title-col {
      flex: 1;
      min-width: 0;
    }

    .name {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    .type-col {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
      max-width: 55%;
      min-width: 0;
    }

    .type-badge {
      display: inline-flex;
      align-items: center;
      font-size: 11px;
      font-weight: 500;
      padding: 2px 8px;
      border-radius: 10px;
      background: rgba(27, 150, 255, 0.15);
      color: #0062c4;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }

    .geolevel {
      font-size: 11px;
      font-weight: 500;
      color: #2aa66e;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }

    .meta {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      font-size: 12px;
      color: #888;
      margin-top: 3px;
    }

    .meta strong {
      color: #555;
      font-weight: 500;
    }

    .meta .sep {
      color: #ccc;
    }

    .description {
      font-size: 12px;
      color: #777;
      margin-top: 4px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .visibility-btn {
      flex-shrink: 0;
      margin-top: -2px;
      width: 26px;
      height: 26px;
      padding: 0;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: #888;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition:
        background 0.15s,
        color 0.15s;
    }
    .visibility-btn:hover {
      background: #eef3f8;
      color: #0078d4;
    }
    .visibility-btn svg {
      width: 16px;
      height: 16px;
    }

    :host([hidden]) {
      opacity: 0.55;
    }
    :host([hidden]) .visibility-btn {
      color: #b0b0b0;
    }
  `;
__decorate([
    property({ type: Object })
], ZoneCard.prototype, "zone", void 0);
__decorate([
    property({ type: String })
], ZoneCard.prototype, "zoneTypeName", void 0);
__decorate([
    property({ type: String })
], ZoneCard.prototype, "geolevelName", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ZoneCard.prototype, "selected", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ZoneCard.prototype, "hidden", void 0);
__decorate([
    state()
], ZoneCard.prototype, "pickerOpen", void 0);
ZoneCard = __decorate([
    localized(),
    customElement('zone-card')
], ZoneCard);
export { ZoneCard };
//# sourceMappingURL=ZoneCard.js.map